import {
  type CurrencyCode,
  LIVE_ORDER_ITEM_STATUSES,
  ORDER_ITEM_STATUSES,
  parseStockQuantity,
  QUANTITY_SCALE,
  quantity,
} from "@tovi/shared";
import type { CartLine, LineOption } from "./cart.ts";
import {
  activeBill,
  addressOf,
  adoptBill,
  adoptServerLines,
  type Bill,
  type BillBook,
  type BillKey,
  closeBill,
  EMPTY_BOOK,
  failBill,
  isEmpty,
  learnOrderId,
  markFired,
  openBill,
  setActive,
  setCovers,
  setDiscount,
  setHeld,
  setTaxExempt,
  setLines,
} from "./bills.ts";
import type { Till } from "./till.ts";
import { indexedDbBillStorage, MemoryBillStorage, type BillStorage } from "./bill-storage.ts";
import { api } from "./api.ts";

/**
 * The till's open bills, and the queue operations that keep them real.
 *
 * A store rather than a hook, for the same reason `Till` is one: a service
 * outlives every render, every screen change and the app being killed.
 *
 * Every mutation goes through one promise chain. Clearing a table while a
 * drain is deciding whether that table's create has gone is a genuine race,
 * and the two answers ("drop it from the queue" and "cancel it on the server")
 * are not interchangeable.
 */

type Listener = () => void;

/** What the server sends back for an order, in the shape this file needs. */
export type OrderResponse = {
  id: string;
  orderNo?: string | null;
  tableId?: string | null;
  tableName?: string | null;
  peopleCount?: number | null;
  diningStyle?: "dine_in" | "take_away";
  currency?: string;
  createdAt?: string;
  isHeld?: boolean;
  isTaxExempt?: boolean;
  items?: {
    id: string;
    name: string;
    quantity: string;
    /** `returned` and `voided` have left the bill; the rest still count. */
    status?: (typeof ORDER_ITEM_STATUSES)[number];
    /** Thousandths, like every quantity. A partial return leaves the rest. */
    returnedQuantity?: string;
    unitPrice: string;
    specifications?: { name: string; price: string }[];
    toppings?: { name: string; price: string }[];
    note?: string | null;
  }[];
};

export class BillBookStore {
  #book: BillBook = EMPTY_BOOK;
  #listeners = new Set<Listener>();
  #storage: BillStorage;
  #till: Till;
  /** The serialisation point. Every mutation waits its turn on this. */
  #queue: Promise<unknown> = Promise.resolve();

  constructor(till: Till, storage: BillStorage = defaultStorage()) {
    this.#till = till;
    this.#storage = storage;
  }

  async start(): Promise<() => void> {
    this.#book = (await this.#storage.read()) ?? EMPTY_BOOK;
    this.#emit();

    const stopSent = this.#till.onSent((operation, response) => {
      const answer = response as OrderResponse | null;
      if (!answer?.id) return;
      // Only a create names a bill; everything else is addressed to one that
      // already has its id.
      if (this.#book.bills.some((bill) => bill.key === operation.id)) {
        void this.#serialise(() => this.#commit(learnOrderId(this.#book, operation.id, answer)));
        return;
      }

      // Every other route answers with the whole order, and a fired line comes
      // back carrying the id each later amendment is addressed by. Taking them
      // is what makes voiding a line this till fired possible at all.
      if (answer.items) {
        const lines = liveLinesOf(answer);
        void this.#serialise(() => this.#commit(adoptServerLines(this.#book, answer.id, lines)));
      }
    });

    const stopRejected = this.#till.onRejected((operation, message) => {
      if (!this.#book.bills.some((bill) => bill.key === operation.id)) return;
      void this.#serialise(() => this.#commit(failBill(this.#book, operation.id, message)));
    });

    await this.recover();
    return () => {
      stopSent();
      stopRejected();
    };
  }

  /**
   * Puts the book back in step with the queue and the server after a restart.
   *
   * The case that needs it: the app was killed between a create being sent and
   * its id being written down. The create is gone from the queue and the bill
   * still has no id, so it is re-queued **under the same operation id** — which
   * travels as `Idempotency-Key`, so the server answers with the bill that
   * already exists rather than opening a second one on the table.
   *
   * That replay is only safe because a bill is opened *empty*. If a create
   * ever carried lines, replaying it would be a stale write.
   */
  async recover(): Promise<void> {
    await this.#serialise(async () => {
      const pending = await this.#till.outbound.pending();
      for (const bill of this.#book.bills) {
        if (bill.orderId !== null || bill.status === "failed") continue;
        if (pending.some((operation) => operation.id === bill.key)) continue;
        await this.#till.queue("/store/orders", { method: "POST", body: bodyOf(bill) }, bill.key);
      }
    });
    void this.#till.drain();
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  book(): BillBook {
    return this.#book;
  }

  active(): Bill | null {
    return activeBill(this.#book);
  }

  /** Covers confirmed on a table, or take-away chosen. Either opens a bill. */
  async open(table: { id: string; name: string } | null, covers: number | null): Promise<void> {
    await this.#serialise(async () => {
      const key = crypto.randomUUID();
      const opened = openBill(this.#book, {
        key,
        table,
        covers,
        openedAt: new Date().toISOString(),
      });
      await this.#commit(opened);
      // The operation's id is the bill's key, which is what lets the create be
      // replayed and what every later operation depends on.
      await this.#till.queue(
        "/store/orders",
        { method: "POST", body: bodyOf(activeBill(opened)!) },
        key,
      );
    });
    void this.#till.drain();
  }

  /** Pull a bill opened on another terminal onto this one. Online only. */
  async adopt(orderId: string): Promise<void> {
    const order = await api<OrderResponse>(`/store/orders/${orderId}`);
    await this.#serialise(() =>
      this.#commit(
        adoptBill(
          this.#book,
          {
            id: order.id,
            orderNo: order.orderNo ?? "",
            tableId: order.tableId ?? null,
            tableName: order.tableName ?? null,
            peopleCount: order.peopleCount ?? null,
            diningStyle: order.diningStyle ?? "dine_in",
            lines: liveLinesOf(order),
            openedAt: order.createdAt ?? new Date().toISOString(),
            isHeld: order.isHeld ?? false,
            isTaxExempt: order.isTaxExempt ?? false,
          },
          crypto.randomUUID(),
        ),
      ),
    );
  }

  async setActive(key: BillKey | null): Promise<void> {
    await this.#serialise(() => this.#commit(setActive(this.#book, key)));
  }

  async setLines(lines: readonly CartLine[]): Promise<void> {
    await this.#serialise(() => this.#commit(setLines(this.#book, lines)));
  }

  /**
   * Correct the covers on the bill that is already open.
   *
   * Queued like everything else, so a cashier who recounts a table while the
   * link is down is not told to remember it: the operation waits behind the
   * create it depends on, and `/covers` asks for nothing the seating did not.
   */
  async setCovers(key: BillKey, covers: number): Promise<void> {
    await this.#serialise(async () => {
      const bill = this.#book.bills.find((row) => row.key === key);
      if (!bill?.table || bill.covers === covers) return;

      const address = addressOf(bill, "/covers");
      await this.#till.queue(address.path, {
        method: "POST",
        body: { peopleCount: covers },
        ...(address.dependsOn ? { dependsOn: address.dependsOn } : {}),
      });
      await this.#commit(setCovers(this.#book, key, covers));
    });
    void this.#till.drain();
  }

  /**
   * A whole-bill discount, which the server prices rather than the till.
   *
   * `isMoney` because this changes what is owed: an operation dropped by
   * last-write-wins would leave the cart showing a discounted total while
   * settlement charged the undiscounted one, which is the divergence the
   * queue exists to prevent. The server replaces rather than accumulates, so
   * `0` clears a discount and is a legitimate amount to send.
   */
  async setDiscount(amount: bigint): Promise<void> {
    await this.#serialise(async () => {
      const bill = activeBill(this.#book);
      if (!bill || bill.discountAmount === amount) return;

      const address = addressOf(bill, "/discount");
      await this.#till.queue(address.path, {
        method: "POST",
        isMoney: true,
        body: { amount: amount.toString(), reason: null, promoId: null },
        ...(address.dependsOn ? { dependsOn: address.dependsOn } : {}),
      });
      await this.#commit(setDiscount(this.#book, amount));
    });
    void this.#till.drain();
  }

  /**
   * Park a bill, or bring one back (`ORDER_PENDING_RESUME`).
   *
   * Keyed, because resuming happens from the strip while another bill is open.
   * A bill whose create is still queued addresses this the same way every other
   * mutation does, so parking one taken offline survives the drain.
   */
  async setHeld(key: BillKey, held: boolean): Promise<void> {
    await this.#serialise(async () => {
      const bill = this.#book.bills.find((row) => row.key === key);
      if (!bill || bill.isHeld === held) return;

      const address = addressOf(bill, held ? "/hold" : "/resume");
      await this.#till.queue(address.path, {
        method: "POST",
        ...(address.dependsOn ? { dependsOn: address.dependsOn } : {}),
      });
      await this.#commit(setHeld(this.#book, key, held));
    });
    void this.#till.drain();
  }

  /**
   * Settle this bill tax-free, or put the tax back (`ORDER_FREE_VAT`).
   *
   * `isMoney` for the same reason a discount is: it changes what is owed, and
   * an exemption dropped for being stale would leave the cart showing a total
   * with no tax on it that settlement then taxed.
   */
  async setTaxExempt(key: BillKey, exempt: boolean): Promise<void> {
    await this.#serialise(async () => {
      const bill = this.#book.bills.find((row) => row.key === key);
      if (!bill || bill.isTaxExempt === exempt) return;

      const address = addressOf(bill, "/tax-exempt");
      await this.#till.queue(address.path, {
        method: "POST",
        isMoney: true,
        body: { isTaxExempt: exempt, reason: null },
        ...(address.dependsOn ? { dependsOn: address.dependsOn } : {}),
      });
      await this.#commit(setTaxExempt(this.#book, key, exempt));
    });
    void this.#till.drain();
  }

  /**
   * Void or return a line the station already has (`ORDER_RETURN` for a
   * return; a void needs only `orders:write`).
   *
   * Addressed by the **order item** id, which is why a fired line's key is the
   * server's id rather than a local one. There is no local commit: the answer
   * carries the order and `onSent` takes the lines from it, so what the cart
   * shows after this is what the server holds — which is the only safe way to
   * edit food that is already being cooked.
   */
  async amendLine(
    key: BillKey,
    itemId: string,
    action: "void" | "return",
    reason: string | null = null,
  ): Promise<void> {
    await this.#serialise(async () => {
      const bill = this.#book.bills.find((row) => row.key === key);
      if (!bill) return;

      const address = addressOf(bill, `/items/${itemId}`);
      await this.#till.queue(address.path, {
        method: "PATCH",
        isMoney: true,
        body: { action, reason },
        ...(address.dependsOn ? { dependsOn: address.dependsOn } : {}),
      });
    });
    void this.#till.drain();
  }

  /**
   * Fold other bills into this one, and close them.
   *
   * **Online only**, like `adopt`. Both sides are named by their *server* ids,
   * so a bill whose create is still queued cannot be either end of a merge —
   * there is no id to put in the body, and `dependsOn` fills one token in a
   * path, not a list in a payload. A cashier offline is told, rather than
   * given an operation that can never drain.
   */
  async merge(key: BillKey, sourceKeys: readonly BillKey[]): Promise<void> {
    const bill = this.#book.bills.find((row) => row.key === key);
    const sources = this.#book.bills.filter((row) => sourceKeys.includes(row.key));
    const sourceOrderIds = sources.map((row) => row.orderId).filter((id): id is string => !!id);
    if (!bill?.orderId || sourceOrderIds.length === 0) return;

    const order = await api<OrderResponse>(`/store/orders/${bill.orderId}/merge`, {
      method: "POST",
      body: { sourceOrderIds },
    });

    await this.#serialise(async () => {
      // The sources are closed on the server, so they leave the book here too.
      let book = this.#book;
      for (const source of sources) book = closeBill(book, source.key);
      await this.#commit(adoptServerLines(book, order.id, liveLinesOf(order)));
    });
  }

  /**
   * Move lines onto a bill of their own — the separate-bills request.
   *
   * **Online only**, and for a second reason on top of the one merge has: the
   * new bill's id is the server's answer, so there is nothing to adopt until it
   * has answered. The lines are named by their order-item ids, which is why
   * only fired lines can be split.
   */
  async split(key: BillKey, itemIds: readonly string[]): Promise<void> {
    const bill = this.#book.bills.find((row) => row.key === key);
    if (!bill?.orderId || itemIds.length === 0) return;

    const created = await api<OrderResponse>(`/store/orders/${bill.orderId}/split`, {
      method: "POST",
      body: { itemIds: [...itemIds], tableId: null },
    });
    // The source kept whatever did not move, and only it knows what that is.
    const source = await api<OrderResponse>(`/store/orders/${bill.orderId}`);

    await this.#serialise(async () => {
      const withSource = adoptServerLines(this.#book, source.id, liveLinesOf(source));
      await this.#commit(
        adoptBill(
          withSource,
          {
            id: created.id,
            orderNo: created.orderNo ?? "",
            tableId: created.tableId ?? null,
            tableName: created.tableName ?? null,
            peopleCount: created.peopleCount ?? null,
            diningStyle: created.diningStyle ?? "dine_in",
            lines: liveLinesOf(created),
            openedAt: created.createdAt ?? new Date().toISOString(),
            isHeld: created.isHeld ?? false,
            isTaxExempt: created.isTaxExempt ?? false,
          },
          crypto.randomUUID(),
        ),
      );
    });
  }

  /** Send the unfired lines to the station. § A: an increment, not a reprint. */
  async fire(): Promise<void> {
    await this.#serialise(async () => {
      const bill = activeBill(this.#book);
      if (!bill) return;
      const unfired = bill.lines.filter((line) => !line.isFired);
      if (unfired.length === 0) return;

      const address = addressOf(bill, "/items");
      await this.#till.queue(address.path, {
        method: "POST",
        body: { lines: unfired.map(lineToServer) },
        ...(address.dependsOn ? { dependsOn: address.dependsOn } : {}),
      });
      await this.#commit(markFired(this.#book, bill.key));
    });
    void this.#till.drain();
  }

  /**
   * Let a table go.
   *
   * Two different undos, and telling them apart is the whole reason `discard`
   * reports what it removed: a create still sitting on this device never
   * reached anyone, so the honest thing is to stop it leaving. One the server
   * has seen is cancelled, which is audited, because somebody else's screen
   * has already shown that table as taken.
   */
  async release(key: BillKey): Promise<void> {
    await this.#serialise(async () => {
      const bill = this.#book.bills.find((row) => row.key === key);
      if (!bill || !isEmpty(bill)) return;

      const removed = await this.#till.discard(key);
      if (removed.length === 0 && bill.orderId) {
        await this.#till.queue(`/store/orders/${bill.orderId}/cancel`, {
          method: "POST",
          // English on purpose: `cancelReason` is stored on the order and read
          // back in back-office reports, so a Khmer till must not put Khmer
          // into a merchant's report data.
          body: { reason: "Seating released at the till" },
        });
      }
      await this.#commit(closeBill(this.#book, key));
    });
    void this.#till.drain();
  }

  /** The bill has been paid; it leaves this till's book. */
  async close(key: BillKey): Promise<void> {
    await this.#serialise(() => this.#commit(closeBill(this.#book, key)));
  }

  async #commit(book: BillBook): Promise<void> {
    this.#book = book;
    await this.#storage.write(book);
    this.#emit();
  }

  #serialise<T>(run: () => Promise<T>): Promise<T> {
    const next = this.#queue.then(run, run);
    // Swallowed on the chain only: a failure must not wedge every later
    // mutation behind it, but the caller still sees its own rejection.
    this.#queue = next.catch(() => undefined);
    return next;
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }
}

function bodyOf(bill: Bill) {
  return {
    tableId: bill.table?.id ?? null,
    diningStyle: bill.diningStyle,
    peopleCount: bill.covers,
    // Empty on purpose: the bill opens when the table is seated, and the lines
    // follow when they are fired. See `bills.ts`.
    lines: [],
  };
}

function lineToServer(line: CartLine) {
  return {
    itemId: line.id,
    quantity: (Number(line.quantity.value) / 10 ** line.quantity.scale).toString(),
    // What the sheet chose. Priced rather than merely named, because the
    // server folds these into the line price it settles on: a fire that sent
    // the words and not the money would put a total on the receipt that the
    // till never showed.
    specifications: (line.specifications ?? []).map(onTheWire),
    toppings: (line.toppings ?? []).map(onTheWire),
    // The one thing on the line the station cannot derive from the item, so it
    // travels with the fire rather than being read back off the cashier.
    note: line.note ?? null,
  };
}

const onTheWire = (option: LineOption) => ({ name: option.name, price: option.price.toString() });

/**
 * The lines an order still has, as the cart understands them.
 *
 * A voided or fully returned line has **left the bill** — the server drops it
 * out of `orderAmount` — so a cart that kept drawing it would price a total the
 * settlement does not charge. A partial return leaves what did not come back.
 */
export function liveLinesOf(order: OrderResponse): CartLine[] {
  const currency = order.currency ?? "USD";
  return (order.items ?? [])
    .filter((item) =>
      item.status ? (LIVE_ORDER_ITEM_STATUSES as readonly string[]).includes(item.status) : true,
    )
    .map((item) => lineFromServer(item, currency))
    .filter((line) => line.quantity.value > 0n);
}

function lineFromServer(
  item: NonNullable<OrderResponse["items"]>[number],
  currency: string,
): CartLine {
  return {
    key: item.id,
    id: item.id,
    name: item.name,
    // The bill's own currency, not this till's guess at one: an adopted bill
    // was priced somewhere else and its money has to keep saying what it is.
    unitPrice: { amount: BigInt(item.unitPrice), currency: currency as CurrencyCode },
    // Scale 3 throughout: `parseStockQuantity` answers in thousandths.
    // What did not come back. A line returned in part keeps the rest, and the
    // server has already taken the returned share out of `orderAmount`.
    quantity: quantity(
      parseStockQuantity(item.quantity) -
        (item.returnedQuantity ? parseStockQuantity(item.returnedQuantity) : 0n),
      QUANTITY_SCALE,
    ),
    isFired: true,
    // Named and not priced again: `unitPriceSnapshot` is the base plus every
    // option, so a `modifiers` entry here would charge each of them twice.
    ...(item.specifications?.length
      ? { specifications: item.specifications.map(fromTheWire) }
      : {}),
    ...(item.toppings?.length ? { toppings: item.toppings.map(fromTheWire) } : {}),
    ...(item.note ? { note: item.note } : {}),
  };
}

const fromTheWire = (option: { name: string; price: string }): LineOption => ({
  name: option.name,
  price: BigInt(option.price),
});

function defaultStorage(): BillStorage {
  return typeof indexedDB === "undefined"
    ? new MemoryBillStorage()
    : indexedDbBillStorage(indexedDB);
}
